import { ButtonGroup, Image, Flex, Text, useDisclosure, HStack, Box, Stack } from '@chakra-ui/react';
import React from 'react';
import Button from '@laliga-cdac2/button';
import { DownloadIcon, LigaFDownloadIcon, EyeIcon, LigaFEyeIcon, RemoveIcon, LigaFRemoveIcon, LigaFDotsHorizontalIcon } from '@laliga-cdac2/icons';
import Popover from '@laliga-cdac2/popover';
import IconButton from '@laliga-cdac2/iconbutton';
import Checkbox from '@laliga-cdac2/checkbox';
import { CheckIcon } from '@chakra-ui/icons';
import { Text as Text$1, Stack as Stack$1 } from '@chakra-ui/layout';
import { Tooltip } from '@chakra-ui/tooltip';
import { truncate } from 'lodash-es';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var AttachButton = function AttachButton(_ref) {
  var label = _ref.label,
      attachButtonProps = _ref.attachButtonProps,
      onAddFile = _ref.onAddFile;
  return React.createElement(Button, Object.assign({
    as: "label",
    colorScheme: "secondaryColor",
    cursor: "pointer",
    borderRadius: "3px"
  }, attachButtonProps), label, React.createElement("input", {
    type: "file",
    style: {
      display: 'none'
    },
    onChange: function onChange(e) {
      return onAddFile(e.target.files);
    }
  }));
};

var DOCUMENT_ULOADER_THEME = {
  colors: {
    highlightedColor: {
      "default": 'corporatePurple.500',
      ligaF: 'terciaryColor.500'
    }
  },
  icons: {
    downloadIcon: {
      "default": DownloadIcon,
      ligaF: LigaFDownloadIcon
    },
    eyeIcon: {
      "default": EyeIcon,
      ligaF: LigaFEyeIcon
    },
    removeIcon: {
      "default": RemoveIcon,
      ligaF: LigaFRemoveIcon
    }
  }
};
var BYTES = 1024;
/**
 * Checks if the file size exceeds the specified maximum size
 *
 * @param {File} file - The file to check the size of
 * @param {number} maxSizeMB - The maximum allowed file size in megabytes
 * @returns {boolean} - Returns true if the file size exceeds the maximum size, otherwise false
 */

function isFileSizeExceeded(file, maxSizeMB) {
  var maxSizeBytes = maxSizeMB * BYTES * BYTES;
  return file.size > maxSizeBytes;
}
/**
 * Retrieves the file extension from the file name
 *
 * @param {File} file - The file to extract the extension from
 * @returns {string | undefined} - The file extension or undefined if it cannot be determined
 */


function getFileExtension(_ref) {
  var name = _ref.name;
  return name.split('.').pop();
}
/**
 * Checks if the file type is allowed based on the specified allowed type
 *
 * @param {File} file - The file to check the type of
 * @param {FileType} allowedType - The allowed file type
 * @returns {boolean} - Returns true if the file type is allowed, otherwise false
 */


function isFileTypeAllowed(file, allowedType) {
  var fileExtension = getFileExtension(file);
  return fileExtension ? allowedType.toLowerCase() === fileExtension.toLowerCase() : false;
}
/**
 * Validates a file based on its size and type
 *
 * @param {File} file - The file to validate
 * @param {number} maxSizeMB - The maximum allowed file size in megabytes
 * @param {FileType} allowedType - The allowed file type
 * @returns {IFileError | undefined} - Returns an object containing the file
 * and error message if the file is invalid, otherwise undefined
 */


function validateFile(file, allowedType, maxSizeMB) {
  var fileExtension = getFileExtension(file);
  var error;

  if (maxSizeMB && isFileSizeExceeded(file, maxSizeMB)) {
    error = "El archivo " + file.name + " excede el tama\xF1o m\xE1ximo permitido de " + maxSizeMB + "MB.";
  }

  if (fileExtension && !isFileTypeAllowed(file, allowedType)) {
    error = "El tipo de archivo " + fileExtension + " no est\xE1 permitido.";
  }

  return error ? {
    file: file,
    error: error
  } : undefined;
}
/**
 * Validates if at least one file is required
 *
 * @param {boolean} required - indicates at least one file is required
 * @param {number} remainingFiles - The number of remaining files
 * @returns {IFileError | undefined} - Returns an object containing the error
 * message if remainig files is one and a file is required, otherwise undefined
 */


function validateRequired(required, remainingFiles) {
  var error;

  if (required && remainingFiles === 1) {
    error = "Se debe adjuntar al menos un archivo.";
  }

  return error ? {
    error: error
  } : undefined;
}
/**
 * Retrieves the first file from the file list
 *
 * @param {FileList} fileList - The list of files
 * @returns {File | undefined} - The first file from the list, or undefined if the list is empty
 */


function getFile(fileList) {
  return Array.from(fileList).shift();
}

function getJustifyContent(hasState, readOnly) {
  if (!hasState) return 'flex-end';
  return readOnly ? 'flex-start' : 'space-between';
}

var ActionButtons = function ActionButtons(_ref) {
  var id = _ref.id,
      avoidDeletion = _ref.avoidDeletion,
      readOnly = _ref.readOnly,
      required = _ref.required,
      remainingFiles = _ref.remainingFiles,
      variant = _ref.variant,
      onError = _ref.onError,
      onDelete = _ref.onDelete,
      onPreview = _ref.onPreview,
      onDownload = _ref.onDownload;
  var DownloadIcon = DOCUMENT_ULOADER_THEME['icons']['downloadIcon'][variant];
  var RemoveIcon = DOCUMENT_ULOADER_THEME['icons']['removeIcon'][variant];
  var EyeIcon = DOCUMENT_ULOADER_THEME['icons']['eyeIcon'][variant];

  var handleOnDelete = function handleOnDelete(id) {
    var error = validateRequired(!!required, remainingFiles);
    error && onError && onError(error);
    onDelete && onDelete(id);
  };

  if (!onDownload && !onPreview && !onDelete) return null;
  return React.createElement(ButtonGroup, {
    gridGap: "2"
  }, onDownload && React.createElement(IconButton, {
    type: "button",
    "aria-label": "download",
    variant: "outline",
    color: "secondaryColor.500",
    display: "contents",
    icon: React.createElement(DownloadIcon, {
      sx: {
        w: '14px',
        h: '14px'
      }
    }),
    onClick: function onClick() {
      return onDownload(id);
    }
  }), onPreview && React.createElement(IconButton, {
    type: "button",
    "aria-label": "preview",
    variant: "outline",
    color: "secondaryColor.500",
    display: "contents",
    icon: React.createElement(EyeIcon, {
      sx: {
        w: '14px',
        h: '14px'
      }
    }),
    onClick: function onClick() {
      return onPreview(id);
    }
  }), !readOnly && !!!avoidDeletion && onDelete && React.createElement(IconButton, {
    type: "button",
    "aria-label": "delete",
    variant: "outline",
    color: "errorColor",
    display: "contents",
    icon: React.createElement(RemoveIcon, {
      sx: {
        w: '14px',
        h: '14px'
      }
    }),
    onClick: function onClick() {
      return handleOnDelete(id);
    }
  }));
};

var FileItem = function FileItem(_ref) {
  var _file$summary;

  var file = _ref.file,
      readOnly = _ref.readOnly,
      fileValidationKey = _ref.fileValidationKey,
      onValidate = _ref.onValidate;
  if (file.logo) return React.createElement(Image, {
    alt: "fileLogo",
    src: file.logo,
    width: "40px"
  });
  return React.createElement(Flex, {
    maxWidth: "min-content",
    gridGap: "1.5",
    overflow: "hidden"
  }, !readOnly && onValidate && fileValidationKey && React.createElement(Checkbox, {
    isChecked: (_file$summary = file.summary) == null ? void 0 : _file$summary[fileValidationKey],
    colorScheme: "primaryColor",
    size: "sm",
    onChange: function onChange() {
      return onValidate(file.id);
    }
  }), React.createElement(Text, {
    fontSize: "14px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis"
  }, file.name));
};

var _excluded = ["files", "readOnly", "onValidate", "fileValidationKey"];

var PopoverContent = function PopoverContent(_ref) {
  var files = _ref.files,
      readOnly = _ref.readOnly,
      onValidate = _ref.onValidate,
      fileValidationKey = _ref.fileValidationKey,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var remainingFiles = files.length;
  return React.createElement(Flex, {
    direction: "column",
    gridGap: "1",
    width: "272px"
  }, files.map(function (file) {
    return React.createElement(Flex, {
      key: file.id,
      gridGap: "2",
      alignItems: "center",
      justify: "space-between"
    }, React.createElement(FileItem, {
      file: file,
      readOnly: readOnly,
      fileValidationKey: fileValidationKey,
      onValidate: onValidate
    }), React.createElement(ActionButtons, Object.assign({
      id: file.id,
      readOnly: readOnly,
      remainingFiles: remainingFiles,
      avoidDeletion: file.avoidDeletion
    }, rest)));
  }));
};

var _excluded$1 = ["files", "infoButtonLabel", "infoButtonProps", "infoButtonLabelProps"];

var UploaderInfoButton = function UploaderInfoButton(_ref) {
  var files = _ref.files,
      infoButtonLabel = _ref.infoButtonLabel,
      infoButtonProps = _ref.infoButtonProps,
      infoButtonLabelProps = _ref.infoButtonLabelProps,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  var _useDisclosure = useDisclosure(),
      _onOpen = _useDisclosure.onOpen,
      onClose = _useDisclosure.onClose,
      isOpen = _useDisclosure.isOpen;

  return React.createElement(Popover, {
    isOpen: isOpen,
    trigger: "click",
    placement: "bottom-center",
    containerProps: {
      boxShadow: '0px 1px 10px 0px #00000022'
    },
    content: React.createElement(PopoverContent, Object.assign({
      files: files
    }, rest)),
    onClose: onClose,
    onOpen: function onOpen() {
      return files.length && _onOpen();
    }
  }, React.createElement(HStack, Object.assign({
    role: "button",
    height: "36px",
    minWidth: "233px",
    bg: files.length ? 'primaryColor.500' : 'corporateBeige.500',
    borderRadius: "2xl",
    justifyContent: "space-around",
    pointerEvents: files.length ? 'auto' : 'none'
  }, infoButtonProps), React.createElement(Text, Object.assign({
    color: files.length ? 'white' : 'primaryFontColor',
    fontWeight: files.length ? 'bold' : 'normal',
    fontSize: "md"
  }, infoButtonLabelProps), infoButtonLabel || files.length + " documentos a\xF1adidos"), React.createElement(Box, {
    as: "span",
    cursor: files.length ? 'pointer' : 'none'
  }, React.createElement(LigaFDotsHorizontalIcon, {
    color: "white",
    boxSize: "20px"
  }))));
};

function getCheckIconColor(count, total) {
  return total > count ? 'corporateBeige.800' : 'corporateGreen.600';
}
/**
 *Formats a label so that the first letter is capitalized.
 *
 *@param {string} label - The label to format.
 *@returns {string} - The formatted label.
 */


function formatLabel(label) {
  if (!label) return '';
  return label.charAt(0).toUpperCase() + label.slice(1);
}
/**
 * Increments the count for a given key in the count object.
 * If the key already exists, it is incremented by 1; otherwise, it is initialized to 1.
 *
 * @param {string} key The key to increment in the count object.
 * @param {Object} addedKeysCount The count object to update.
 * @returns The updated count object.
 */


function updateKeyCount(key, addedKeysCount) {
  var updatedKeysCount = _extends({}, addedKeysCount);

  updatedKeysCount[key] = (updatedKeysCount[key] || 0) + 1;
  return updatedKeysCount;
}
/**
 * Generates a summary of keys and their occurrences in a list of files.
 *
 * @param {IFile[]} files - The list of files.
 * @returns {Object} - The summary of keys and their occurrences.
 */


function getSummary(files) {
  var addedKeysCount = {};
  files == null ? void 0 : files.forEach(function (_ref) {
    var summary = _ref.summary;
    summary && Object.keys(summary).forEach(function (key) {
      if (summary[key]) addedKeysCount = updateKeyCount(key, addedKeysCount);
    });
  });
  return addedKeysCount;
}

var SummaryItem = function SummaryItem(_ref) {
  var label = _ref.label,
      count = _ref.count,
      total = _ref.total;
  return React.createElement(HStack, {
    gridGap: "0.5"
  }, React.createElement(CheckIcon, {
    boxSize: "0.8em",
    color: getCheckIconColor(count, total)
  }), React.createElement(Text, {
    fontSize: "sm"
  }, label, " ", count, "/", total));
};

var UploaderSummary = function UploaderSummary(_ref) {
  var files = _ref.files,
      summaryProps = _ref.summaryProps;
  var summaryKeysCount = getSummary(files);
  return React.createElement(Stack, Object.assign({
    spacing: 2
  }, summaryProps), Object.entries(summaryKeysCount).map(function (_ref2) {
    var key = _ref2[0],
        count = _ref2[1];
    return React.createElement(SummaryItem, {
      key: "item_" + key,
      label: formatLabel(key),
      count: count,
      total: files.length
    });
  }));
};

var UTF8_ASTERISK = "*";

var RequiredIndicator = function RequiredIndicator(_ref) {
  var variant = _ref.variant,
      hasError = _ref.hasError;
  return React.createElement(Text$1, {
    as: "span",
    paddingLeft: "0.5",
    color: hasError ? 'stateColors.deletionColor' : DOCUMENT_ULOADER_THEME['colors']['highlightedColor'][variant]
  }, UTF8_ASTERISK);
}; // Max length for truncating the title in different scenarios


var TRUNCATE_LIMIT = {
  WITHOUT_EXTRA_INFO: 120,
  WITH_FILE_TYPE: 95,
  WITH_FILE_SIZE: 95,
  ALL: 55
};
/**
 * Returns the maximum length for truncating the title based on the visibility
 * of additional information like file type and max file size.
 *
 * @returns {number} The maximum length for truncating the title.
 */

var getTruncateLimit = function getTruncateLimit(showMaxSize, showFileType) {
  if (!showMaxSize && !showFileType) return TRUNCATE_LIMIT['WITHOUT_EXTRA_INFO'];
  if (!showMaxSize && showFileType) return TRUNCATE_LIMIT['WITH_FILE_TYPE'];
  if (showMaxSize && !showFileType) return TRUNCATE_LIMIT['WITH_FILE_SIZE'];
  return TRUNCATE_LIMIT['ALL'];
};

var Title = function Title(_ref) {
  var title = _ref.title,
      showFileType = _ref.showFileType,
      showMaxSize = _ref.showMaxSize;
  return React.createElement(Tooltip, {
    label: title,
    hasArrow: true
  }, "" + truncate(title.toUpperCase(), {
    length: getTruncateLimit(showMaxSize, showFileType)
  }));
};

var UploaderTitle = function UploaderTitle(_ref) {
  var title = _ref.title,
      fileType = _ref.fileType,
      required = _ref.required,
      showMaxSize = _ref.showMaxSize,
      showFileType = _ref.showFileType,
      maxSize = _ref.maxSize,
      hasError = _ref.hasError,
      variant = _ref.variant,
      isFilesEmpty = _ref.isFilesEmpty; // It shows the asterisk (*) in error if at least one file is required and there are no attachments.
  // This validation is triggered when files are deleted

  var requiredError = !!(hasError && isFilesEmpty);
  return React.createElement(Stack$1, null, React.createElement(Text, {
    fontSize: "md",
    fontWeight: "semibold",
    textAlign: "center"
  }, React.createElement(Title, {
    title: title,
    showFileType: showFileType,
    showMaxSize: showMaxSize
  }), showFileType && React.createElement(Text, {
    as: "span",
    fontSize: "md",
    fontWeight: "semibold",
    textAlign: "center"
  }, " (" + fileType + ")"), required && React.createElement(RequiredIndicator, {
    hasError: requiredError,
    variant: variant
  })), showMaxSize && React.createElement(Text, {
    m: 0,
    fontSize: 13,
    textAlign: "center"
  }, "Tama\xF1o m\xE1x. " + maxSize + "MB"));
};

var _excluded$2 = ["files", "title", "attachButtonLabel", "infoButtonLabel", "required", "maxSizePerFile", "fileType", "hasError", "showMaxSizeInfo", "showFileType", "readOnly", "titleProps", "attachButtonProps", "infoButtonProps", "infoButtonLabelProps", "summaryProps", "bodyTemplate", "headerTemplate", "variant", "fileValidationKey", "onError", "onAddFile"];

var DocumentUpload = function DocumentUpload(_ref) {
  var _ref$files = _ref.files,
      files = _ref$files === void 0 ? [] : _ref$files,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? '' : _ref$title,
      _ref$attachButtonLabe = _ref.attachButtonLabel,
      attachButtonLabel = _ref$attachButtonLabe === void 0 ? 'Adjuntar' : _ref$attachButtonLabe,
      infoButtonLabel = _ref.infoButtonLabel,
      required = _ref.required,
      maxSizePerFile = _ref.maxSizePerFile,
      _ref$fileType = _ref.fileType,
      fileType = _ref$fileType === void 0 ? 'PDF' : _ref$fileType,
      hasError = _ref.hasError,
      showMaxSizeInfo = _ref.showMaxSizeInfo,
      showFileType = _ref.showFileType,
      readOnly = _ref.readOnly,
      titleProps = _ref.titleProps,
      attachButtonProps = _ref.attachButtonProps,
      infoButtonProps = _ref.infoButtonProps,
      infoButtonLabelProps = _ref.infoButtonLabelProps,
      summaryProps = _ref.summaryProps,
      bodyTemplate = _ref.bodyTemplate,
      headerTemplate = _ref.headerTemplate,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant,
      fileValidationKey = _ref.fileValidationKey,
      onError = _ref.onError,
      onAddFile = _ref.onAddFile,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var hasSummary = !!files.length && (files == null ? void 0 : files.some(function (file) {
    return file.summary;
  }));
  var isFilesEmpty = !files.length;

  var handleAddFiles = function handleAddFiles(fileList) {
    if (!(fileList != null && fileList.length)) return;
    var file = getFile(fileList);
    var error = file && validateFile(file, fileType, maxSizePerFile);

    if (error && onError) {
      onError(error);
    } else if (!error && onAddFile) {
      onAddFile(fileList);
    }
  };

  return React.createElement(Flex, {
    width: "16.5rem",
    height: "16.5rem",
    overflow: "hidden",
    direction: "column",
    padding: "4",
    borderTop: "3px solid",
    borderColor: hasError ? 'stateColors.deletionColor' : 'transparent',
    boxShadow: "0px 1px 10px 0px #00000022",
    gridGap: "2",
    justifyContent: "space-between"
  }, React.createElement(Box, Object.assign({
    h: "full"
  }, titleProps), headerTemplate && headerTemplate() || React.createElement(UploaderTitle, {
    title: title,
    fileType: fileType,
    showFileType: showFileType,
    showMaxSize: !!(showMaxSizeInfo && maxSizePerFile),
    maxSize: maxSizePerFile,
    required: required,
    hasError: hasError,
    isFilesEmpty: isFilesEmpty,
    variant: variant
  })), bodyTemplate && bodyTemplate(files) || React.createElement(Flex, {
    direction: "column",
    justifyContent: "space-between",
    h: "full",
    gridGap: "2.5"
  }, React.createElement(UploaderInfoButton, Object.assign({
    files: files,
    readOnly: readOnly,
    variant: variant,
    fileValidationKey: fileValidationKey,
    infoButtonProps: infoButtonProps,
    infoButtonLabel: infoButtonLabel,
    infoButtonLabelProps: infoButtonLabelProps,
    onError: onError
  }, rest)), React.createElement(HStack, {
    justifyContent: getJustifyContent(hasSummary, readOnly)
  }, hasSummary && React.createElement(UploaderSummary, {
    files: files,
    summaryProps: summaryProps
  }), !readOnly && React.createElement(AttachButton, {
    label: attachButtonLabel,
    attachButtonProps: attachButtonProps,
    onAddFile: handleAddFiles
  }))));
};

export default DocumentUpload;